import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ArrowRight, Search, MessageSquareText, Database, X, BarChart3, Reply, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { SortableTh } from '@/components/ui/sortable-table';
import { fetchValidationProgress, type ValidationProgress } from '@/hooks/useValidationStats';
import {
  fetchQueueForView, saveQueueView, saveQueueMine, saveQueueSort, saveQueueSearch,
  readQueueState, sortQueueRows, queueSortKeyExists, saveQueueFavOwner,
  type QueueView, type QueueSort, type QueueSortKey,
} from '@/lib/queue-view';
import { addFavorite, removeFavorite, fetchFavoriteUsers } from '@/hooks/useFavorites';
import { useDmpLookup, JvidLink } from '@/components/dmp/DmpLookup';
import { useAuth } from '@/hooks/useAuth';
import type { AddressValidationQueueItem, AddressValidationStatus } from '@uniticket/shared';

// The selected view survives leaving the page (user ask 2026-08-03) AND drives the « Ticket suivant »
// chain in the validation window (user ask 2026-08-04) — both live in @/lib/queue-view so they can
// never drift apart.
type Filter = QueueView;

// The client-side sort on the date columns (user ask 2026-08-03) now lives in @/lib/queue-view with
// the rest of the queue state — the « Ticket suivant » chain has to reproduce this exact order
// (Anouk 2026-08-10), so the ordering can't stay private to this page.

const TERMINAL_TICKET_STATUSES = ['RESOLVED', 'CLOSED'];
const OPEN_VALIDATION_STATUSES = ['PENDING', 'ESCALATED', 'WAITING_BUILD'];

function StatusPill({ status }: { status: AddressValidationStatus }) {
  const styles: Record<AddressValidationStatus, string> = {
    PENDING: 'bg-amber-50 text-amber-700',
    VALIDATED: 'bg-emerald-50 text-emerald-700',
    REJECTED: 'bg-rose-50 text-rose-700',
    ESCALATED: 'bg-orange-50 text-orange-700',
    WAITING_BUILD: 'bg-sky-50 text-sky-700',
  };
  return (
    <span className={cn('text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide', styles[status])}>
      {status.toLowerCase().replace(/_/g, ' ')}
    </span>
  );
}

// Priority pill — HIGH/URGENT stand out (the working queue is sorted by priority) while NORMAL/LOW stay muted.
function PriorityPill({ priority }: { priority: string }) {
  const styles: Record<string, string> = {
    URGENT: 'bg-red-100 text-red-700',
    HIGH: 'bg-amber-50 text-amber-700',
    NORMAL: 'bg-slate-100 text-slate-500',
    LOW: 'bg-slate-50 text-slate-400',
  };
  return (
    <span className={cn('text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide', styles[priority] ?? 'bg-slate-100 text-slate-500')}>
      {priority.toLowerCase()}
    </span>
  );
}

// Marks a synthetic test/demo row (only shown to admins via the "show test tickets" toggle). Real
// Uniticket rows carry no badge — keeps the default validator view clean.
function OriginBadge({ origin }: { origin: 'seed' | 'dev' }) {
  const { t } = useTranslation('tickets');
  const isDev = origin === 'dev';
  return (
    <span
      title={t(isDev ? 'validation.queue.originDevHint' : 'validation.queue.originSeedHint')}
      className={cn(
        'ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full align-middle',
        isDev ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500',
      )}
    >
      {t(isDev ? 'validation.queue.originDev' : 'validation.queue.originSeed')}
    </span>
  );
}

// A HUMAN reply landed after the Send-to-Build hand-off: the row is back in PENDING (top of the list)
// and this badge explains WHY it reappeared. Cleared by the next save / escalate / re-send.
function BuildReplyBadge({ at }: { at: string }) {
  const { t } = useTranslation('tickets');
  return (
    <span
      title={formatDate(at)}
      className="ml-2 inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 align-middle"
    >
      <MessageSquareText className="w-3 h-3" /> {t('validation.queue.buildReplyBadge')}
    </span>
  );
}

// The linked ticket was resolved/closed DIRECTLY in Uniticket while the validation was still open —
// the row lives under the « Résolu (Uniticket) » chip and comes back by itself if the ticket reopens.
function TicketResolvedBadge({ status }: { status: string }) {
  const { t } = useTranslation('tickets');
  return (
    <span
      title={t('validation.queue.ticketResolvedHint')}
      className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full align-middle bg-emerald-50 text-emerald-700"
    >
      {t(status === 'CLOSED' ? 'validation.queue.ticketClosedBadge' : 'validation.queue.ticketResolvedBadge')}
    </span>
  );
}

// WHY a row is parked (user ask 2026-08-06, F12): « en attente » used to show a bare PENDING /
// WAITING_BUILD pill, so a reviewer scanning the waiting list could not tell a Build hand-off from a
// question sent to the operator from an escalation — « tu peux avoir : Escaladé, questions posées ».
// The matching « réponse reçue » signals are the Build/operator reply badges rendered next to it.
function WaitingReasonBadge({ item }: { item: AddressValidationQueueItem }) {
  const { t } = useTranslation('tickets');
  const buildParked = item.handedOffAt != null && item.buildReplyAt == null;
  const escalated = item.status === 'ESCALATED';
  // Deliberately NOT covering the open-question case: the « Question posée » badge below already says
  // it, and rendering both put the same words twice on the same row.
  if (!buildParked && !escalated) return null;
  const [key, style, at] = escalated
    ? ['waitingEscalated', 'bg-orange-100 text-orange-700', item.escalatedAt]
    : ['waitingBuild', 'bg-sky-100 text-sky-700', item.handedOffAt];
  return (
    <span
      title={at ? formatDate(at) : undefined}
      className={cn('ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase', style)}
    >
      {escalated && item.assignedToName
        ? t('validation.queue.waitingEscalatedTo', { name: item.assignedToName })
        : t(`validation.queue.${key}`)}
    </span>
  );
}

// Who holds the FILE in AV (user ask 2026-08-06, F11) — set by our own actions (question, Build
// parking, escalation, a Pending with a message), never by Uniticket's routing. Shown only when the
// owner is someone ELSE: seeing your own name on half the queue teaches nothing, whereas « Chez
// Frederic » answers the one question the reviewer has — why is this row here and who is on it.
// …and its counterpart on MY OWN rows. Those float above the calm part of the list (user ask
// 2026-08-07: « on va te le laisser en priorité »), and a row that jumps the queue with nothing to
// explain why is just confusing — this is Fred's « comment est-ce qu'on le voit ici ? ».
function OwnerBadge({ name, mine }: { name: string; mine?: boolean }) {
  const { t } = useTranslation('tickets');
  return (
    <span
      title={mine ? t('validation.queue.ownedByMeTitle') : t('validation.queue.ownedByTitle', { name })}
      className={cn(
        'ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold',
        mine ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700',
      )}
    >
      {mine ? t('validation.queue.ownedByMe') : t('validation.queue.ownedBy', { name })}
    </span>
  );
}

// Who holds the ticket in Uniticket (user ask 2026-08-06, F10) — shown in the « Tous » audit view only,
// where it answers "who has it" without pretending to say who WORKS it. The masking it was designed to
// explain is off (config.queue.hideAssigned): assigned_to turned out to be routing, not a hand-over.
function AssigneeBadge({ name }: { name: string }) {
  const { t } = useTranslation('tickets');
  return (
    <span
      title={t('validation.queue.assignedToTitle', { name })}
      className="ml-1.5 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
    >
      {t('validation.queue.assignedTo', { name })}
    </span>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// « Rythme de validation » — bandeau d'avancement en tête de la file (demande user 2026-08-03).
// ADMINS SEULEMENT (outil de pilotage) : le composant n'est monté que si isAdmin, et l'endpoint est
// de toute façon gated SYSTEM_ADMIN côté serveur. « Traité » = verdict final seul ; un échec de
// chargement fait simplement disparaître le bandeau — la file ne doit jamais dépendre des stats.
function ProgressBanner() {
  const { t } = useTranslation('tickets');
  const [progress, setProgress] = useState<ValidationProgress | null>(null);
  useEffect(() => {
    let alive = true;
    fetchValidationProgress()
      .then(({ data }) => { if (alive) setProgress(data); })
      .catch(() => { /* best-effort */ });
    return () => { alive = false; };
  }, []);
  if (!progress) return null;
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white px-4 py-3"
      title={t('validation.queue.statsAdminHint')}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-sm font-semibold text-slate-700">
          {t('validation.queue.statsProgress', { treated: progress.treated, total: progress.total })}
          <span className="ml-2 font-bold text-emerald-700">{progress.pct} %</span>
        </p>
        <Link
          to="/admin/validation-stats"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-rose hover:underline"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          {t('validation.queue.statsLink')}
        </Link>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
          style={{ width: `${Math.min(100, progress.pct)}%` }}
        />
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        {t('validation.queue.statsRemaining', { count: progress.open })}
        {' · '}
        {t('validation.queue.statsDelta', { month: progress.monthToDate, week: progress.weekToDate })}
      </p>
    </div>
  );
}

export function AddressValidationQueue() {
  const { t } = useTranslation(['tickets', 'common']);
  const navigate = useNavigate();
  const { open: openDmpLookup } = useDmpLookup();
  const { isAdmin, user } = useAuth();
  const [items, setItems] = useState<AddressValidationQueueItem[]>([]);
  // The whole queue state is restored in ONE read, so the saved sort can be validated against the
  // view it is restored with (an « Envoyé le » sort means nothing on the validated list).
  const [restored] = useState(readQueueState);
  const [filter, setFilter] = useState<Filter>(restored.view);
  useEffect(() => { saveQueueView(filter); }, [filter]);
  // F11 — « à moi ou à personne ». Persisted like the view (and read back by the « Ticket suivant »
  // chain, so the window walks the same list the reviewer is looking at).
  const [mine, setMine] = useState<boolean>(restored.mine);
  useEffect(() => { saveQueueMine(mine); }, [mine]);
  // null = the server's business order (escalated/replies/mine/priority first). PERSISTED since the
  // 2026-08-10 review (« il ne sauvegarde pas mon filtre sur la date. Ça, c'est chiant ») and now
  // KEPT across a view change when the column still exists there — it used to be blanked on every
  // change, which also meant a restored sort was wiped on mount. Dropped when the column is absent,
  // never applied invisibly (see queueSortKeyExists).
  const [sort, setSort] = useState<QueueSort | null>(restored.sort);
  useEffect(() => { saveQueueSort(sort); }, [sort]);
  useEffect(() => { setSort((s) => (s && queueSortKeyExists(filter, s.key) ? s : null)); }, [filter]);
  // « À traiter » (escalated) is only offered to the configured escalation target and to admins
  // (user decision 2026-07-30 — the personal « À traiter par moi » view was dropped as redundant
  // while there is a single target).
  const [isEscalationTarget, setIsEscalationTarget] = useState(false);
  const [loading, setLoading] = useState(true);
  // Free-text search (jvid / ticket number / comment). `searchInput` is the raw field; `search` is the
  // debounced value actually sent, so each keystroke doesn't fire a request.
  // Restored too (2026-08-10): the « Ticket suivant » chain must walk the SEARCHED list, and coming
  // back from a ticket to an unfiltered queue is the irritant the 03-08 ask already fixed for the
  // view. It stays visible in the box, with its clear button — a restored search never hides itself.
  const [searchInput, setSearchInput] = useState(restored.search);
  const [search, setSearch] = useState(restored.search);
  useEffect(() => { saveQueueSearch(search); }, [search]);
  useEffect(() => {
    const h = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(h);
  }, [searchInput]);

  // ⭐ De qui on regarde les favoris (null = les miens). Persisté à côté de la vue, et pour la même
  // raison : la fenêtre de validation le relit pour que « Ticket suivant » suive la liste AFFICHÉE.
  const [favOwnerId, setFavOwnerId] = useState<string | null>(restored.favOwnerId);
  useEffect(() => { saveQueueFavOwner(favOwnerId); }, [favOwnerId]);
  const [favUsers, setFavUsers] = useState<{ id: string; displayName: string }[]>([]);
  useEffect(() => {
    // Chargé seulement quand la vue Favoris s'ouvre : inutile de peupler un sélecteur qu'on ne voit pas.
    if (filter !== 'FAVORITES' || favUsers.length) return;
    fetchFavoriteUsers().then((r) => setFavUsers(r.data)).catch(() => setFavUsers([]));
  }, [filter, favUsers.length]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Test/demo tickets are no longer surfaced here (checkbox removed — user ask 2026-07-30);
      // the server enforces Uniticket-only regardless.
      const res = await fetchQueueForView(filter, false, search, mine, favOwnerId);
      setItems(res.data);
      setIsEscalationTarget(res.meta?.isEscalationTarget ?? false);
    } catch (err: any) {
      toast.error(err.message || t('tickets:validation.queue.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filter, search, mine, favOwnerId, t]);

  // Bascule de l'étoile. OPTIMISTE avec retour arrière : le geste doit répondre tout de suite (on
  // marque en parcourant la file), mais un échec réseau ne doit pas laisser une étoile mensongère.
  async function toggleFavorite(ticketId: string, next: boolean) {
    setItems((list) => list.map((it) => (it.lastTicketId === ticketId ? { ...it, isFavorite: next } : it)));
    try {
      if (next) await addFavorite(ticketId);
      else await removeFavorite(ticketId);
      // Dans la vue Favoris, retirer l'étoile doit faire sortir la ligne — sinon on croit que ça a raté.
      if (!next && filter === 'FAVORITES') await load();
    } catch (err: any) {
      setItems((list) => list.map((it) => (it.lastTicketId === ticketId ? { ...it, isFavorite: !next } : it)));
      toast.error(err?.message || t('tickets:validation.queue.favoriteError'));
    }
  }

  useEffect(() => { load(); }, [load]);

  const filters: { key: Filter; label: string }[] = [
    { key: 'PENDING', label: t('tickets:validation.queue.filterPending') },
    { key: 'VALIDATED', label: t('tickets:validation.queue.filterValidated') },
    { key: 'REJECTED', label: t('tickets:validation.queue.filterRejected') },
    // Escalated view: only the escalation target and admins (kept while selected so the active
    // option never vanishes mid-use).
    ...(isAdmin || isEscalationTarget || filter === 'ESCALATED'
      ? [{ key: 'ESCALATED' as Filter, label: t('tickets:validation.queue.filterEscalated') }]
      : []),
    { key: 'WAITING_BUILD', label: t('tickets:validation.queue.filterWaitingBuild') },
    { key: 'TICKET_RESOLVED', label: t('tickets:validation.queue.filterTicketResolved') },
    // ⭐ Ouverte à tous, pas seulement aux admins — contrairement à « À traiter » juste au-dessus.
    { key: 'FAVORITES', label: t('tickets:validation.queue.filterFavorites') },
    { key: 'ALL', label: t('tickets:validation.queue.filterAll') },
  ];

  // Which date columns this view shows — asked of queueSortKeyExists, the SAME rule that decides
  // whether a saved sort still applies. One answer to "does this view have this column", so a column
  // can never be rendered without its sort surviving, nor a sort survive onto an absent column.
  // Verdict-date column (user ask 2026-07-29): in the Validated/Rejected views the "Ticket created"
  // date reads as "when was this handled" and misleads (a May ticket validated today looks old) —
  // show the actual verdict date there. Meaningless for Pending, so the column only exists on these views.
  const showVerdictDate = queueSortKeyExists(filter, 'verdictAt');
  // « Transmis le » (user ask 2026-07-30): when the escalation was handed over — shown in the
  // escalated view (itself restricted to the escalation target + admins).
  const showEscalatedDate = queueSortKeyExists(filter, 'escalatedAt');
  // « Envoyé le » (user ask 2026-07-30): when the request left — Build hand-off or operator
  // question, whichever parked the row in the waiting view.
  const showSentDate = queueSortKeyExists(filter, 'sentAt');

  // Sorted view of the loaded rows. Rows without the date always sink to the bottom, whatever the
  // direction. First click = most recent first (the common intent), second click flips.
  const shown = sortQueueRows(items, sort);
  // Rendu délégué au composant partagé (voir components/ui/sortable-table) — mêmes en-têtes
  // cliquables sur les trois tables de l'app. Comportement identique à avant : 1er clic = desc sur
  // une colonne de date, valeurs vides toujours en bas.
  const sortableTh = (label: string, key: QueueSortKey) => (
    <SortableTh
      key={key}
      label={label}
      sortKey={key}
      kind="date"
      sort={sort}
      onSort={(next) => setSort(next as QueueSort)}
      title={t('tickets:validation.queue.sortHint')}
      className="px-4"
    />
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <ClipboardCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('tickets:validation.queue.title')}</h2>
          <p className="text-sm text-slate-500">{t('tickets:validation.queue.subtitle', { count: items.length })}</p>
        </div>
      </div>

      {/* Avancement global — pilotage, admins seulement (demande user 2026-08-03) */}
      {isAdmin && <ProgressBanner />}

      {/* Search — jvid / ticket number / comment (debounced, server-side) + the DMP console entry. */}
      <div className="max-w-xl">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('tickets:validation.queue.searchPlaceholder')}
              className="pl-9 pr-9"
              aria-label={t('tickets:validation.queue.searchPlaceholder')}
            />
            {searchInput !== '' && (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setSearch(''); }}
                aria-label={t('tickets:validation.queue.clearSearch')}
                title={t('tickets:validation.queue.clearSearch')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => openDmpLookup()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 hover:border-brand-rose/40 hover:text-brand-rose"
          >
            <Database className="w-4 h-4" />
            {t('tickets:dmpLookup.title')}
          </button>
        </div>
        {/* jvid-shaped search → offer the DMP console too (the queue only filters TICKETS). */}
        {/^\d{6,9}$/.test(search) && (
          <button
            type="button"
            onClick={() => openDmpLookup(Number(search))}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-brand-rose hover:underline"
          >
            <Database className="w-3.5 h-3.5" />
            {t('tickets:validation.queue.dmpLookupHint', { jvid: search })}
          </button>
        )}
      </div>

      {/* View dropdown (user ask 2026-07-30 — the chip row got too crowded; « En attente » stays
          the default). The « À traiter par moi » option keeps its conditional visibility. */}
      <div className="flex items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          aria-label={t('tickets:validation.queue.filterAria')}
          className="h-10 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-rose/40"
        >
          {filters.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        {/* ⭐ « Favoris de… ». Anouk, 03-09 : « par personne, MAIS visibles par les autres + filtre par
            personne » — parce que « lui il ne sait pas que moi j'ai l'information ». Ouvert à TOUS les
            internes : Fred et Jonathan sont les deux seuls validateurs, et le réserver aux admins les
            aurait exclus l'un de l'autre, c'est-à-dire manqué la demande. */}
        {filter === 'FAVORITES' && (
          <select
            value={favOwnerId ?? ''}
            onChange={(e) => setFavOwnerId(e.target.value || null)}
            aria-label={t('tickets:validation.queue.favOwnerAria')}
            className="h-10 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-rose/40"
          >
            <option value="">{t('tickets:validation.queue.favOwnerMine')}</option>
            {favUsers.filter((u) => u.id !== user?.id).map((u) => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
          </select>
        )}
        {/* F11 — « avoir un filtre sur soi ou sur personne » (Fred, réunion 2026-08-06). Hidden on
            « Tous », which is the exhaustive audit listing and ignores it server-side.
            Masqué aussi sur « Favoris » : une liste qu'on a soi-même constituée n'a pas à être
            re-filtrée, et le serveur l'ignore de toute façon sur cette vue. */}
        {filter !== 'ALL' && filter !== 'FAVORITES' && (
          <label
            title={t('tickets:validation.queue.mineHint')}
            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm hover:border-slate-300"
          >
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => setMine(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer accent-brand-rose"
            />
            {t('tickets:validation.queue.mineFilter')}
          </label>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">
          {t('tickets:validation.queue.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                {/* ⭐ Colonne d'étoile, tout à gauche. Sans libellé : l'icône se lit seule, et un
                    en-tête ici décalerait la lecture des vraies colonnes. */}
                <th className="w-8 px-2 py-3" aria-label={t('tickets:validation.queue.colFavorite')} />
                <th className="px-4 py-3 font-semibold">{t('tickets:validation.colJvid')}</th>
                <th className="px-4 py-3 font-semibold">{t('tickets:validation.queue.colOperator')}</th>
                <th className="px-4 py-3 font-semibold">{t('tickets:validation.queue.colStatus')}</th>
                <th className="px-4 py-3 font-semibold">{t('tickets:validation.queue.colPriority')}</th>
                <th className="px-4 py-3 font-semibold">{t('tickets:validation.queue.colTicket')}</th>
                <th className="px-4 py-3 font-semibold">{t('tickets:validation.queue.colReviewer')}</th>
                {sortableTh(t('tickets:validation.queue.colTicketCreated'), 'createdAt')}
                {showVerdictDate && sortableTh(
                  t(filter === 'REJECTED' ? 'tickets:validation.queue.colRejectedAt' : 'tickets:validation.queue.colValidatedAt'),
                  'verdictAt',
                )}
                {showEscalatedDate && sortableTh(t('tickets:validation.queue.colEscalatedAt'), 'escalatedAt')}
                {showSentDate && sortableTh(t('tickets:validation.queue.colSentAt'), 'sentAt')}
                {filter === 'FAVORITES' && sortableTh(t('tickets:validation.queue.colFavoritedAt'), 'favoritedAt')}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((it) => {
                const href = it.lastTicketId
                  ? `/tickets/${it.lastTicketId}/address-validation`
                  : null;
                const go = () => { if (href) navigate(href); };
                return (
                  <tr
                    key={it.jvid != null && it.addressCount == null ? `${it.jvid}-${it.operatorId}` : `t-${it.lastTicketId}`}
                    role={href ? 'link' : undefined}
                    tabIndex={href ? 0 : undefined}
                    aria-label={href ? t('tickets:validation.queue.reviewAria', { jvid: it.jvid }) : undefined}
                    onClick={href ? go : undefined}
                    onKeyDown={href ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
                    } : undefined}
                    className={cn(
                      'border-t border-slate-100 transition-colors',
                      href && 'cursor-pointer hover:bg-brand-rose/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose/40 focus-visible:ring-inset',
                    )}
                  >
                    {/* ⭐ L'étoile est TOUJOURS la MIENNE, même en regardant la liste d'un collègue :
                        cliquer ajoute à mes favoris, jamais retirer le sien. `stopPropagation` parce
                        que toute la ligne est un lien vers le ticket. */}
                    <td className="px-2 py-3">
                      {it.lastTicketId ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); void toggleFavorite(it.lastTicketId!, !it.isFavorite); }}
                          title={t(it.isFavorite ? 'tickets:validation.queue.unfavorite' : 'tickets:validation.queue.favorite')}
                          aria-pressed={!!it.isFavorite}
                          className="rounded p-0.5 hover:bg-amber-50"
                        >
                          <Star className={cn('h-4 w-4', it.isFavorite ? 'fill-amber-400 text-amber-500' : 'text-slate-300')} />
                        </button>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {it.jvid != null ? <JvidLink jvid={it.jvid} /> : '—'}
                      {it.noAddress && (
                        <span
                          className="ml-2 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                          title={t('tickets:validation.queue.noAddressTitle')}
                        >
                          {t('tickets:validation.queue.noAddressBadge')}
                        </span>
                      )}
                      {/* N45 ⑥ — une ligne pour tout un ticket à plusieurs adresses, avec sa progression. */}
                      {it.addressCount != null && (
                        <span
                          className="ml-2 whitespace-nowrap text-[10px] font-bold uppercase bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded"
                          title={t('tickets:validation.queue.multiTitle', { count: it.addressCount, done: it.addressesDone ?? 0 })}
                        >
                          {t('tickets:validation.queue.multiBadge', { count: it.addressCount, done: it.addressesDone ?? 0 })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{it.operatorName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <StatusPill status={it.status} />
                      {it.escalated && (
                        <span
                          className="ml-1.5 inline-flex items-center text-[10px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded"
                          title={it.escalationReason || t('tickets:validation.queue.escalatedTitle')}
                        >
                          {t('tickets:validation.queue.escalatedBadge')}
                        </span>
                      )}
                      {/* An escalation handed back with its answer (F4) — the reviewer who escalated
                          finds it at the top of their own queue with the reason it came back. */}
                      {it.returnedAt && (
                        <span
                          title={formatDate(it.returnedAt)}
                          className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-fuchsia-50 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-fuchsia-700"
                        >
                          <Reply className="h-3 w-3" /> {t('tickets:validation.returnedBadge')}
                        </span>
                      )}
                      {/* Plus de pastille « Remplacée → » ni « Liée par erreur » (N31.1, décision du 30-09) : la ligne
                          d'un JVID remplacé ou délié est supprimée, et le geste se lit dans l'historique du ticket. */}
                      {it.buildReplyAt && <BuildReplyBadge at={it.buildReplyAt} />}
                      {it.operatorReplyAt ? (
                        <span
                          className="ml-1.5 inline-flex items-center text-[10px] font-bold uppercase bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded"
                          title={t('tickets:validation.queue.operatorReplyTitle', { date: new Date(it.operatorReplyAt).toLocaleString() })}
                        >
                          {t('tickets:validation.queue.operatorReplyBadge')}
                        </span>
                      ) : it.questionAskedAt && (it.status === 'PENDING' || it.status === 'WAITING_BUILD') ? (
                        <span
                          className="ml-1.5 inline-flex items-center text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded"
                          title={t('tickets:validation.queue.questionAskedTitle', { date: new Date(it.questionAskedAt).toLocaleString() })}
                        >
                          {t('tickets:validation.queue.questionAskedBadge')}
                        </span>
                      ) : null}
                      {it.ticketStatus != null
                        && TERMINAL_TICKET_STATUSES.includes(it.ticketStatus)
                        && OPEN_VALIDATION_STATUSES.includes(it.status)
                        && <TicketResolvedBadge status={it.ticketStatus} />}
                      {/* F12 — why the row is parked; F10 — who holds it. */}
                      <WaitingReasonBadge item={it} />
                      {/* Audit view only: the Uniticket assignee is a ROUTING artefact, not a claim
                          (probe 2026-08-06 — the dossiers Frederic validated carry Jérémy's name), so
                          badging every working row with it would mislabel the day's work. */}
                      {filter === 'ALL' && it.assignedToName && <AssigneeBadge name={it.assignedToName} />}
                      {/* F11 — whose desk the file is on: someone else's, or mine (which is also why
                          the row sits high in the list). */}
                      {it.ownerId && (
                        it.ownerId === user?.id
                          ? <OwnerBadge name="" mine />
                          : it.ownerName && <OwnerBadge name={it.ownerName} />
                      )}
                      {/* Escalated before the target was recorded: kept visible for the chain rather
                          than silently dropped by the personal « À traiter » scoping (F13). */}
                      {it.status === 'ESCALATED' && it.escalationTargetKnown === false && (
                        <span
                          title={t('tickets:validation.queue.escalationTargetUnknownTitle')}
                          className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700"
                        >
                          {t('tickets:validation.queue.escalationTargetUnknown')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><PriorityPill priority={it.priority} /></td>
                    <td className="px-4 py-3 text-slate-600">
                      {it.ticketNumber ?? '—'}
                      {it.origin !== 'uniticket' && <OriginBadge origin={it.origin} />}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{it.validatedByName ?? '—'}</td>
                    {/* Ticket creation date (user ask 2026-07-27, replaces the last-review timestamp) —
                        the REAL Uniticket created_at (backfilled by the sync since 74b8a9c). */}
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(it.createdAt)}</td>
                    {showVerdictDate && (
                      <td className="px-4 py-3 text-slate-600 font-medium whitespace-nowrap">
                        {it.validatedAt ? formatDate(it.validatedAt) : '—'}
                      </td>
                    )}
                    {showEscalatedDate && (
                      <td className="px-4 py-3 text-slate-600 font-medium whitespace-nowrap">
                        {it.escalatedAt ? formatDate(it.escalatedAt) : '—'}
                      </td>
                    )}
                    {showSentDate && (
                      <td className="px-4 py-3 text-slate-600 font-medium whitespace-nowrap">
                        {(it.handedOffAt ?? it.questionAskedAt) ? formatDate((it.handedOffAt ?? it.questionAskedAt)!) : '—'}
                      </td>
                    )}
                    {filter === 'FAVORITES' && (
                      <td className="px-4 py-3 text-slate-600 font-medium whitespace-nowrap">
                        {it.favoritedAt ? formatDate(it.favoritedAt) : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      {href ? (
                        <ArrowRight className="inline-block w-4 h-4 text-brand-rose/70" aria-hidden="true" />
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
